import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { calc } from "@/lib/sheetEngine";
import { Calculator, X, Delete, ArrowRightLeft, RefreshCw, Loader2 } from "lucide-react";
import { HelpButton } from "@/components/HelpButton";

const KEYS = [
  ["C", "(", ")", "/"],
  ["7", "8", "9", "*"],
  ["4", "5", "6", "-"],
  ["1", "2", "3", "+"],
  ["0", ".", "%", "="],
];

const nf = new Intl.NumberFormat("es-VE", { maximumFractionDigits: 2 });

export function CalculatorWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("calc");
  const [expr, setExpr] = useState("");
  const [history, setHistory] = useState("");
  const ref = useRef(null);

  // ---- Divisas ----
  const [rates, setRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratesErr, setRatesErr] = useState("");
  const [assetCode, setAssetCode] = useState(null);
  const [dir, setDir] = useState("to_ves"); // to_ves | from_ves
  const [amount, setAmount] = useState("");
  const [manual, setManual] = useState("");

  const loadRates = useCallback(async () => {
    setLoadingRates(true);
    setRatesErr("");
    try {
      const { data } = await api.get("/rates");
      setRates(data);
      setManual("");
      setAssetCode((prev) => (prev && data.assets.some((a) => a.code === prev) ? prev : data.assets[0]?.code || null));
    } catch (err) {
      setRatesErr(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingRates(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "divisas" && !rates && !loadingRates && !ratesErr) loadRates();
  }, [tab, rates, loadingRates, ratesErr, loadRates]);

  const asset = useMemo(() => rates?.assets?.find((a) => a.code === assetCode) || rates?.assets?.[0] || null, [rates, assetCode]);

  const rate = useMemo(() => {
    const m = parseFloat(String(manual).replace(",", "."));
    if (!isNaN(m) && m > 0) return m;
    return asset?.rate || 0;
  }, [manual, asset]);

  const result = useMemo(() => {
    const a = parseFloat(String(amount).replace(",", "."));
    if (isNaN(a) || !rate) return null;
    return dir === "to_ves" ? a * rate : a / rate;
  }, [amount, rate, dir]);

  const evaluate = useCallback(() => {
    if (!expr.trim()) return;
    try { const r = calc(expr); setHistory(`${expr} =`); setExpr(String(r)); }
    catch { setHistory("Error"); }
  }, [expr]);

  const press = useCallback((k) => {
    if (k === "C") { setExpr(""); setHistory(""); return; }
    if (k === "=") { evaluate(); return; }
    if (k === "%") { try { const r = calc(expr || "0"); setExpr(String(r / 100)); } catch { /* noop */ } return; }
    setExpr((e) => e + k);
  }, [expr, evaluate]);

  const backspace = () => setExpr((e) => e.slice(0, -1));

  useEffect(() => {
    if (!open || tab !== "calc") return;
    const onKey = (e) => {
      const k = e.key;
      if (k === "Enter" || k === "=") { e.preventDefault(); evaluate(); }
      else if (k === "Escape") setOpen(false);
      else if (k === "Backspace") setExpr((v) => v.slice(0, -1));
      else if (/[0-9+\-*/().%]/.test(k)) setExpr((v) => v + k);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, tab, evaluate]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const TabBtn = ({ id, children }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === id ? "bg-indigo-600 text-white" : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
      data-testid={`calc-tab-${id}`}
    >
      {children}
    </button>
  );

  return createPortal(
    <div ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white shadow-xl shadow-indigo-600/30 transition-all duration-300 hover:scale-105 flex items-center justify-center"
        title="Calculadora rápida"
        data-testid="calculator-toggle"
      >
        {open ? <X size={22} /> : <Calculator size={22} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-5 z-[90] w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-3" data-testid="calculator-panel">
          <div className="flex items-center gap-1 mb-3 bg-slate-100 dark:bg-slate-800/60 rounded-xl p-1">
            <TabBtn id="calc">Calculadora</TabBtn>
            <TabBtn id="divisas">Divisas</TabBtn>
            <HelpButton tool="calculator" />
          </div>

          {tab === "calc" ? (
            <>
              <div className="flex items-center justify-end mb-2 px-1">
                <button onClick={backspace} className="text-slate-400 hover:text-rose-500 transition-colors" title="Borrar" data-testid="calc-backspace"><Delete size={17} /></button>
              </div>
              <div className="bg-slate-50 dark:bg-slate-950 rounded-lg px-3 py-2 mb-2 text-right border border-slate-200 dark:border-slate-800">
                <div className="font-mono2 text-[11px] text-slate-400 h-4 truncate" data-testid="calc-history">{history}</div>
                <div className="font-mono2 text-xl text-slate-900 dark:text-slate-100 truncate min-h-[28px]" data-testid="calc-display">{expr || "0"}</div>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {KEYS.flat().map((k) => {
                  const isOp = ["/", "*", "-", "+", "%"].includes(k);
                  const isEq = k === "=";
                  const isClear = k === "C";
                  const cls = isEq ? "bg-indigo-600 hover:bg-indigo-500 text-white"
                    : isClear ? "bg-rose-500/10 text-rose-500 hover:bg-rose-500/20"
                    : isOp ? "bg-slate-100 dark:bg-slate-800 text-indigo-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                    : "bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700";
                  return <button key={k} onClick={() => press(k)} className={`h-10 rounded-lg font-mono2 text-sm font-medium transition-colors ${cls}`} data-testid={`calc-key-${k}`}>{k}</button>;
                })}
              </div>
            </>
          ) : (
            <div data-testid="fx-panel">
              {loadingRates ? (
                <div className="py-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={22} /></div>
              ) : ratesErr ? (
                <div className="text-center py-4">
                  <p className="font-body text-xs text-rose-500 mb-2">{ratesErr}</p>
                  <button onClick={loadRates} className="text-xs text-indigo-500 hover:text-indigo-400 font-medium inline-flex items-center gap-1" data-testid="fx-retry"><RefreshCw size={13} /> Reintentar</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                    {(rates?.assets || []).map((a) => (
                      <button key={a.code} onClick={() => setAssetCode(a.code)} className={`flex-1 min-w-[60px] py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${asset?.code === a.code ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400"}`} data-testid={`fx-asset-${a.code}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-950 rounded-lg p-2.5 mb-2 border border-slate-200 dark:border-slate-800">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono2 text-[10px] text-slate-400">Tasa del día (Bs. por {asset?.symbol})</span>
                      <button onClick={loadRates} className="text-slate-400 hover:text-indigo-500" title="Actualizar tasa" data-testid="fx-refresh"><RefreshCw size={12} /></button>
                    </div>
                    <input
                      value={manual !== "" ? manual : (asset?.rate ?? "")}
                      onChange={(e) => setManual(e.target.value)}
                      className="w-full bg-transparent font-mono2 text-lg text-slate-900 dark:text-slate-100 focus:outline-none"
                      inputMode="decimal"
                      data-testid="fx-rate"
                    />
                    <span className="font-mono2 text-[9px] text-slate-400">
                      {manual !== "" ? "tasa editada manualmente" : (rates?.updated ? `actualizado ${new Date(rates.updated).toLocaleDateString("es-VE", { day: "2-digit", month: "short" })}` : rates?.source)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 text-center bg-slate-100 dark:bg-slate-800 rounded-lg py-1.5 font-mono2 text-xs font-semibold">{dir === "to_ves" ? asset?.symbol : "Bs. VES"}</div>
                    <button onClick={() => setDir((d) => (d === "to_ves" ? "from_ves" : "to_ves"))} className="w-8 h-8 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white flex items-center justify-center shrink-0" title="Invertir" data-testid="fx-swap"><ArrowRightLeft size={15} /></button>
                    <div className="flex-1 text-center bg-slate-100 dark:bg-slate-800 rounded-lg py-1.5 font-mono2 text-xs font-semibold">{dir === "to_ves" ? "Bs. VES" : asset?.symbol}</div>
                  </div>

                  <input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={dir === "to_ves" ? `Monto en ${asset?.symbol}` : "Monto en Bs."}
                    inputMode="decimal"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 font-mono2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 mb-2"
                    data-testid="fx-amount"
                  />

                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-right">
                    <span className="font-mono2 text-[10px] text-emerald-600 dark:text-emerald-400">{dir === "to_ves" ? "Bolívares" : asset?.label}</span>
                    <div className="font-mono2 text-lg font-semibold text-emerald-600 dark:text-emerald-400" data-testid="fx-result">
                      {result == null ? "—" : (dir === "to_ves" ? `Bs. ${nf.format(result)}` : `${asset?.symbol} ${nf.format(result)}`)}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>,
    document.body
  );
}
